/** External Signals — the market and calendar data Signals reads beside the
 * customer's own figures.
 *
 * One country only: the signed-in account's. There is deliberately no country
 * selector; global series appear and say so. Which series are visible, and how
 * healthy each one is, comes from the server — a source still waiting for a
 * licence never reaches this page.
 *
 * Built phone-first: every grid collapses to one column, filters scroll
 * sideways instead of wrapping, and the detail opens as a bottom sheet on a
 * phone and a side panel on a desktop.
 */
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CalendarDays,
  ChevronRight,
  CloudSun,
  Coins,
  Droplet,
  ExternalLink,
  Fuel,
  Globe2,
  Hotel,
  Info,
  Landmark,
  Loader2,
  MapPin,
  Minus,
  Percent,
  Search,
  Ship,
  Users,
  Wheat
} from 'lucide-react';
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';

import {
  ExternalSignals as Data,
  MarketEvent,
  SeriesHistory,
  Signal,
  SignalStatus,
  Weather,
  apiExternalSignals,
  apiSeriesHistory
} from '@/components/external-signals/api';
import {
  changesInPoints,
  formatPct,
  formatPoints,
  formatValue,
  frequencyLabel,
  periodLabel,
  previousLabel,
  relativeTime,
  searchText,
  shortDate,
  unitLabel
} from '@/components/external-signals/format';
import { Header } from '@/components/header';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { SidebarProvider } from '@/components/ui/sidebar';

import { useIsMobile } from '@/hooks/use-mobile';

const ReactApexChart = lazy(() => import('react-apexcharts'));

const FAMILY_ICON: Record<string, typeof Coins> = {
  fx: Coins,
  inflation: Percent,
  interest_rate: Landmark,
  fuel: Fuel,
  oil: Droplet,
  commodity: Wheat,
  supply_chain: Ship,
  geopolitical: Globe2,
  consumer_confidence: Users,
  tourism: Hotel,
  weather: CloudSun,
  calendar: CalendarDays
};

type ScopeFilter = 'all' | 'country' | 'global';

/** One plain sentence per topic, for someone who has never heard of a
 * "supply chain pressure index". The page fails its purpose if a first-time
 * visitor cannot tell what each topic is from this line alone. */
const TOPIC_PLAIN: Record<string, string> = {
  fx: 'What one US dollar costs in your currency, day by day.',
  inflation: 'How fast everyday prices are rising in your country.',
  interest_rate: "The central bank's rate, which sets the cost of borrowing.",
  fuel: 'What petrol and diesel cost at the pump in your country.',
  oil: 'World crude oil prices, behind fuel and transport costs.',
  commodity:
    'World prices of raw materials: metals, food, cotton, fertilisers.',
  supply_chain: 'How strained factories and shipping are around the world.',
  geopolitical: 'How much the world press writes about conflict and tension.',
  consumer_confidence: 'How confident households feel about spending.',
  tourism: 'How full hotels are and how long visitors stay.',
  weather: 'Temperature where you operate: measured, forecast and usual.',
  calendar: 'Public holidays, Ramadan and Eid, with Hijri dates.'
};

export default function ExternalSignals() {
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [family, setFamily] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Signal | null>(null);

  useEffect(() => {
    apiExternalSignals()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const visible = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.signals.filter(
      (s) =>
        (scope === 'all' || s.scope === scope) &&
        (family === 'all' || s.family === family) &&
        (!q || searchText(s).includes(q))
    );
  }, [data, scope, family, query]);

  if (error) {
    return (
      <Shell>
        <Notice tone="warn">{error}</Notice>
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-10">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading external signals…
        </div>
      </Shell>
    );
  }

  const country = data.country.name;
  const showWeather =
    !!data.weather &&
    scope !== 'global' &&
    (family === 'all' || family === 'weather') &&
    !query;
  const showCalendar =
    scope !== 'global' && (family === 'all' || family === 'calendar') && !query;
  const families = data.families.filter(
    (f) => f.key !== 'weather' && f.key !== 'calendar'
  );
  const cardsByFamily = families
    .map((f) => ({
      family: f,
      cards: visible.filter(
        (s) => s.family === f.key && (s.featured || !!query || family === f.key)
      )
    }))
    .filter((g) => g.cards.length > 0);
  const more = visible.filter((s) => !s.featured && !query && family === 'all');
  const lastUpdate = [
    ...data.signals.map((s) => s.updated_at),
    data.weather?.updated_at,
    data.calendar.updated_at
  ]
    .filter(Boolean)
    .sort()
    .pop();
  const counts = {
    country: data.signals.filter((s) => s.scope === 'country').length + 2,
    global: data.signals.filter((s) => s.scope === 'global').length
  };

  return (
    <Shell>
      {/* Title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
            External signals
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            The world around your business, for {country} and worldwide.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium"
            style={{
              background: 'var(--sig-tint)',
              color: 'var(--sig-blue-deep)'
            }}
          >
            <MapPin className="h-3.5 w-3.5" />
            {country}
          </span>
          <span>Updated {relativeTime(lastUpdate)}</span>
        </div>
      </div>

      {/* What this page is, in plain words, and every topic it covers. A
          first-time visitor should understand both before seeing a number. */}
      <div
        className="rounded-2xl border p-4 sm:p-5 space-y-4"
        style={{
          background: 'var(--sig-panel)',
          borderColor: 'var(--sig-border)'
        }}
      >
        <div className="space-y-1.5 max-w-3xl">
          <h2 className="text-[15px] font-semibold">What is this page?</h2>
          <p
            className="text-sm leading-relaxed"
            style={{ color: 'var(--sig-body)' }}
          >
            Your sales and costs do not move in a vacuum. Signals tracks prices,
            the currency, interest rates, holidays and the weather, so that when
            you ask why your numbers changed it can show what else changed at
            the same time — the timing, never the blame.
          </p>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
            What Signals watches · tap a topic to see it
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
            {data.families.map((f) => {
              const Icon = FAMILY_ICON[f.key] ?? Info;
              const n =
                f.key === 'weather' || f.key === 'calendar'
                  ? 1
                  : data.signals.filter((x) => x.family === f.key).length;
              const local =
                f.key === 'weather' ||
                f.key === 'calendar' ||
                data.signals.some(
                  (x) => x.family === f.key && x.scope === 'country'
                );
              const global = data.signals.some(
                (x) => x.family === f.key && x.scope === 'global'
              );
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => {
                    setScope('all');
                    setQuery('');
                    setFamily(f.key);
                    document
                      .getElementById('signals-content')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="text-left rounded-xl border p-2.5 sm:p-3 flex flex-col sm:flex-row gap-1.5 sm:gap-2.5 items-start transition-colors hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  style={{
                    background: 'var(--sig-surface)',
                    borderColor:
                      family === f.key ? 'var(--sig-blue)' : 'var(--sig-border)'
                  }}
                >
                  <span
                    className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'var(--sig-tint)' }}
                  >
                    <Icon
                      className="h-4 w-4"
                      style={{ color: 'var(--sig-blue)' }}
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-baseline gap-x-1.5">
                      <span className="text-[13px] font-semibold">
                        {f.label}
                      </span>
                      <span className="text-[11px] text-muted-foreground tabular-nums">
                        {n === 1 ? '1 indicator' : `${n} indicators`}
                      </span>
                    </span>
                    <span className="block text-xs leading-snug text-muted-foreground mt-0.5">
                      {TOPIC_PLAIN[f.key] ?? f.blurb}
                    </span>
                    <span
                      className="block text-[10.5px] mt-1"
                      style={{ color: 'var(--sig-blue-deep)' }}
                    >
                      {[local ? country : null, global ? 'Global' : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* At a glance */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
        <Tile label="Signals">
          <span className="text-2xl font-semibold tabular-nums">
            {counts.country + counts.global}
          </span>
          <span className="text-xs text-muted-foreground">
            {counts.country} for {country} · {counts.global} global
          </span>
        </Tile>
        <Tile label="Next holiday">
          {data.calendar.upcoming[0] ? (
            <>
              <span className="text-sm font-semibold leading-snug line-clamp-2">
                {data.calendar.upcoming[0].name}
              </span>
              <span className="text-xs text-muted-foreground">
                {shortDate(data.calendar.upcoming[0].start)}
                {data.calendar.upcoming[0].status === 'expected'
                  ? ' · expected'
                  : ''}
              </span>
            </>
          ) : (
            <span className="text-sm text-muted-foreground">Nothing ahead</span>
          )}
        </Tile>
        <Tile
          label={data.weather ? `Weather · ${data.weather.city}` : 'Weather'}
        >
          <WeatherGlance weather={data.weather} />
        </Tile>
        <Tile label="Health">
          <HealthGlance data={data} />
        </Tile>
      </div>

      {/* Filters — sticky so they stay reachable on a long phone scroll */}
      <div
        id="signals-content"
        className="sticky top-0 z-10 -mx-4 sm:mx-0 px-4 sm:px-0 py-2.5 space-y-2.5"
        style={{ background: 'hsl(var(--background))' }}
      >
        <div className="flex gap-2 items-center">
          <div
            className="inline-flex rounded-lg p-0.5 shrink-0"
            style={{ background: 'var(--sig-tab-active)' }}
            role="tablist"
            aria-label="Scope"
          >
            {(
              [
                ['all', 'All'],
                ['country', country],
                ['global', 'Global']
              ] as [ScopeFilter, string][]
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={scope === k}
                onClick={() => setScope(k)}
                className="px-3 py-1.5 text-xs sm:text-[13px] rounded-md transition-colors whitespace-nowrap"
                style={{
                  background:
                    scope === k ? 'var(--sig-surface)' : 'transparent',
                  color: scope === k ? 'var(--sig-ink)' : 'var(--sig-muted)',
                  fontWeight: scope === k ? 600 : 400,
                  boxShadow: scope === k ? 'var(--sig-shadow-card)' : undefined
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-0">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search signals"
              className="pl-8 h-9 text-sm"
              aria-label="Search signals"
            />
          </div>
        </div>
        <div className="sig-noscrollbar flex gap-1.5 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <Chip active={family === 'all'} onClick={() => setFamily('all')}>
            Everything
          </Chip>
          {data.families.map((f) => {
            const Icon = FAMILY_ICON[f.key] ?? Info;
            return (
              <Chip
                key={f.key}
                active={family === f.key}
                onClick={() => setFamily(family === f.key ? 'all' : f.key)}
              >
                <Icon className="h-3.5 w-3.5" />
                {f.label}
              </Chip>
            );
          })}
        </div>
      </div>

      {/* Weather + calendar: always about the account's own country */}
      {showWeather || showCalendar ? (
        <div className="grid gap-3 lg:grid-cols-2">
          {showWeather && data.weather ? (
            <WeatherCard weather={data.weather} />
          ) : null}
          {showCalendar ? <CalendarCard data={data} /> : null}
        </div>
      ) : null}

      {/* Signal cards, by family */}
      {cardsByFamily.map(({ family: f, cards }) => {
        const Icon = FAMILY_ICON[f.key] ?? Info;
        return (
          <section key={f.key} className="space-y-2.5">
            <div className="flex items-baseline gap-2">
              <Icon
                className="h-4 w-4 self-center"
                style={{ color: 'var(--sig-blue)' }}
              />
              <h2 className="text-[15px] font-semibold">{f.label}</h2>
              <span className="text-xs text-muted-foreground truncate">
                {f.blurb}
              </span>
            </div>
            <div className="grid gap-2.5 sm:gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {cards.map((s) => (
                <SignalCard
                  key={s.key}
                  signal={s}
                  country={country}
                  onOpen={() => setOpen(s)}
                />
              ))}
            </div>
          </section>
        );
      })}

      {visible.length === 0 && !showWeather && !showCalendar ? (
        <Notice tone="info">No signal matches this filter.</Notice>
      ) : null}

      {/* Everything else, compact */}
      {more.length ? (
        <section className="space-y-2.5">
          <div className="flex items-baseline gap-2">
            <h2 className="text-[15px] font-semibold">All series</h2>
            <span className="text-xs text-muted-foreground">
              {more.length} more, mostly world commodity prices
            </span>
          </div>
          <SeriesList signals={more} onOpen={setOpen} />
        </section>
      ) : null}

      {/* Market events */}
      {['all', 'interest_rate', 'fx', 'fuel'].includes(family) && !query ? (
        <section className="space-y-2.5">
          <div className="flex items-baseline gap-2">
            <h2 className="text-[15px] font-semibold">Market events</h2>
            <span className="text-xs text-muted-foreground">
              Official decisions and step changes
            </span>
          </div>
          <EventsTimeline events={data.events} country={country} />
        </section>
      ) : null}

      <p className="text-xs text-muted-foreground pt-2 pb-6 leading-relaxed">
        Signals shows these figures beside yours to describe timing and size —
        that two things moved together, not that one caused the other. Each
        source is credited on its card.
      </p>

      <DetailSheet
        signal={open}
        country={country}
        onClose={() => setOpen(null)}
      />
    </Shell>
  );
}

/* ── Layout ─────────────────────────────────────────────────────────────── */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <div className="flex flex-col h-screen w-full">
        <Header />
        <div className="flex-1 overflow-auto">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-5">
            {children}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

function Tile({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-xl border p-3 sm:p-3.5 flex flex-col gap-1 min-w-0"
      style={{
        background: 'var(--sig-panel)',
        borderColor: 'var(--sig-border)'
      }}
    >
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground truncate">
        {label}
      </span>
      {children}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs whitespace-nowrap transition-colors shrink-0"
      style={{
        background: active ? 'var(--sig-blue)' : 'var(--sig-surface)',
        borderColor: active ? 'var(--sig-blue)' : 'var(--sig-border)',
        color: active ? '#fff' : 'var(--sig-body)'
      }}
    >
      {children}
    </button>
  );
}

function Notice({
  tone,
  children
}: {
  tone: 'info' | 'warn';
  children: React.ReactNode;
}) {
  return (
    <div
      className="flex gap-2 rounded-xl border p-3 text-sm"
      style={
        tone === 'warn'
          ? {
              background: 'var(--sig-warn-bg)',
              borderColor: 'var(--sig-warn-border)',
              color: 'var(--sig-warn-text)'
            }
          : { background: 'var(--sig-panel)', borderColor: 'var(--sig-border)' }
      }
    >
      {tone === 'warn' ? (
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
      ) : (
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
      )}
      <div>{children}</div>
    </div>
  );
}

/* ── Small pieces ───────────────────────────────────────────────────────── */

const STATE_COLOUR: Record<string, string> = {
  ok: 'var(--sig-positive)',
  discontinued: 'var(--sig-subtle)',
  delayed: 'var(--sig-warn-icon)',
  failed: 'var(--sig-negative)',
  empty: 'var(--sig-subtle)'
};

function StatusPill({ status }: { status: SignalStatus }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"
      title={status.detail ?? undefined}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{
          background: STATE_COLOUR[status.state] ?? 'var(--sig-subtle)'
        }}
      />
      {status.label}
    </span>
  );
}

function ScopeBadge({ scope, country }: { scope: string; country: string }) {
  const global = scope === 'global';
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium shrink-0"
      style={{
        background: global ? 'var(--sig-chip)' : 'var(--sig-tint)',
        color: global ? 'var(--sig-muted)' : 'var(--sig-blue-deep)'
      }}
    >
      {global ? <Globe2 className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
      {global ? 'Global' : country}
    </span>
  );
}

/** Direction of a change, without calling it good or bad: a falling price is
 * good news for a buyer and bad news for a seller. */
function changeText(
  s: Signal,
  vs: 'previous' | 'year'
): { dir: number | null; text: string } {
  const then = vs === 'previous' ? (s.previous?.value ?? null) : s.year_ago;
  const now = s.latest?.value ?? null;
  if (changesInPoints(s.unit)) {
    return {
      dir: now === null || then === null ? null : now - then,
      text: formatPoints(now, then, s.unit)
    };
  }
  const pct = vs === 'previous' ? s.change_pct : s.yoy_pct;
  return { dir: pct, text: formatPct(pct) };
}

function Change({ signal, vs }: { signal: Signal; vs: 'previous' | 'year' }) {
  const { dir, text } = changeText(signal, vs);
  const label =
    vs === 'previous' ? previousLabel(signal.frequency) : 'vs a year ago';
  const Icon =
    dir === null || dir === 0 ? Minus : dir > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center gap-0.5 text-xs" title={label}>
      <Icon className="h-3.5 w-3.5" style={{ color: 'var(--sig-muted)' }} />
      <span
        className="tabular-nums font-medium"
        style={{ color: 'var(--sig-ink)' }}
      >
        {text}
      </span>
      <span className="text-muted-foreground ml-1">{label}</span>
    </span>
  );
}

function Sparkline({
  values,
  height = 34
}: {
  values: number[];
  height?: number;
}) {
  if (values.length < 2) return <div style={{ height }} />;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * 100;
    const y = 4 + (1 - (v - min) / span) * (height - 8);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const last = pts[pts.length - 1].split(',');
  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height }}
      aria-hidden
    >
      <polyline
        points={`0,${height} ${pts.join(' ')} 100,${height}`}
        fill="var(--sig-tint)"
        stroke="none"
      />
      <polyline
        points={pts.join(' ')}
        fill="none"
        stroke="var(--sig-blue)"
        strokeWidth={1.6}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r={2.2} fill="var(--sig-blue)" />
    </svg>
  );
}

/* ── Cards ──────────────────────────────────────────────────────────────── */

function SignalCard({
  signal: s,
  country,
  onOpen
}: {
  signal: Signal;
  country: string;
  onOpen: () => void;
}) {
  const Icon = FAMILY_ICON[s.family] ?? Info;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left rounded-xl border p-3.5 sm:p-4 flex flex-col gap-2.5 transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-w-0"
      style={{
        background: 'var(--sig-surface)',
        borderColor: 'var(--sig-border)'
      }}
    >
      <div className="flex items-start gap-2.5">
        <span
          className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: 'var(--sig-tint)' }}
        >
          <Icon className="h-4 w-4" style={{ color: 'var(--sig-blue)' }} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold leading-snug line-clamp-2">
            {s.name}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
            {s.source}
          </div>
        </div>
        <ScopeBadge scope={s.scope} country={country} />
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-xl font-semibold tabular-nums tracking-tight">
              {formatValue(s.latest?.value, s.unit)}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {unitLabel(s.unit)}
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground">
            {periodLabel(s.latest?.period, s.frequency)}
          </div>
        </div>
        <div className="w-24 sm:w-28 shrink-0">
          <Sparkline values={s.spark} />
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <Change signal={s} vs="previous" />
        {s.frequency !== 'annual' ? <Change signal={s} vs="year" /> : null}
      </div>

      <div
        className="flex items-center justify-between gap-2 pt-2 border-t text-[11px] text-muted-foreground"
        style={{ borderColor: 'var(--sig-border-light)' }}
      >
        <StatusPill status={s.status} />
        <span className="truncate">
          {frequencyLabel(s.frequency)}
          {s.coverage_from ? ` · since ${s.coverage_from.slice(0, 4)}` : ''}
        </span>
      </div>
    </button>
  );
}

function WeatherGlance({ weather }: { weather: Weather | null }) {
  const d = weather?.latest_observed;
  if (!d || d.tmax === null) {
    return (
      <span className="text-sm text-muted-foreground">No reading yet</span>
    );
  }
  const diff = d.normal_tmax !== null ? d.tmax - d.normal_tmax : null;
  return (
    <>
      <span className="text-2xl font-semibold tabular-nums">
        {d.tmax.toFixed(0)}°
      </span>
      <span className="text-xs text-muted-foreground">
        {diff === null
          ? shortDate(d.day)
          : `${Math.abs(diff).toFixed(1)}° ${diff >= 0 ? 'above' : 'below'} usual · ${shortDate(d.day)}`}
      </span>
    </>
  );
}

function HealthGlance({ data }: { data: Data }) {
  const states = data.signals.map((s) => s.status.state);
  const bad = states.filter((s) => s === 'failed').length;
  const late = states.filter((s) => s === 'delayed').length;
  return (
    <>
      <span className="text-sm font-semibold">
        {bad
          ? `${bad} failed`
          : late
            ? `${late} behind schedule`
            : 'All up to date'}
      </span>
      <span className="text-xs text-muted-foreground">
        {bad || late
          ? 'Their last good figures stay in use.'
          : 'Every source refreshed on time.'}
      </span>
    </>
  );
}

function WeatherCard({ weather: w }: { weather: Weather }) {
  const forecast = w.days.filter((d) => d.basis === 'forecast').slice(0, 14);
  const highs = forecast.flatMap((d) => [d.tmax ?? 0, d.normal_tmax ?? 0]);
  const top = Math.max(...highs, 1);
  const bottom = Math.min(...highs.filter((v) => v > 0), top) - 3;
  const scale = (v: number | null) =>
    v === null ? 0 : Math.max(8, ((v - bottom) / (top - bottom || 1)) * 100);
  return (
    <div
      className="rounded-xl border p-3.5 sm:p-4 space-y-3 min-w-0"
      style={{
        background: 'var(--sig-surface)',
        borderColor: 'var(--sig-border)'
      }}
    >
      <div className="flex items-start gap-2.5">
        <span
          className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: 'var(--sig-tint)' }}
        >
          <CloudSun className="h-4 w-4" style={{ color: 'var(--sig-blue)' }} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold">
            Temperature · {w.city}
          </div>
          <div className="text-[11px] text-muted-foreground">
            Next {forecast.length} days, daily high against the usual for each
            date
          </div>
        </div>
        <StatusPill status={w.status} />
      </div>
      {forecast.length ? (
        <div className="sig-noscrollbar overflow-x-auto -mx-1 px-1">
          <div className="flex items-end gap-1.5 min-w-[440px]">
            {forecast.map((d) => (
              <div
                key={d.day}
                className="flex-1 flex flex-col items-center gap-1 min-w-[26px]"
              >
                <span className="text-[10px] tabular-nums font-medium">
                  {d.tmax !== null ? `${d.tmax.toFixed(0)}°` : ''}
                </span>
                {/* A fixed height, not flex-1: a percentage bar needs a definite parent. */}
                <div className="relative w-full h-20 flex items-end">
                  <div
                    className="w-full rounded-t"
                    style={{
                      height: `${scale(d.tmax)}%`,
                      background: 'var(--sig-node)'
                    }}
                  />
                  {d.normal_tmax !== null ? (
                    <div
                      className="absolute left-0 right-0 border-t-2 border-dashed"
                      style={{
                        bottom: `${scale(d.normal_tmax)}%`,
                        borderColor: 'var(--sig-ink)',
                        opacity: 0.45
                      }}
                    />
                  ) : null}
                </div>
                <span className="text-[10px] text-muted-foreground">
                  {new Date(d.day + 'T00:00:00').getDate()}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No forecast available right now.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="h-2 w-3 rounded-sm"
            style={{ background: 'var(--sig-node)' }}
          />
          Forecast high
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="w-3 border-t-2 border-dashed"
            style={{ borderColor: 'var(--sig-ink)', opacity: 0.45 }}
          />
          Usual (1991–2020)
        </span>
        <span className="truncate">{w.source}</span>
      </div>
    </div>
  );
}

const EVENT_LABEL: Record<string, string> = {
  ramadan: 'Ramadan',
  eid_fitr: 'Eid al-Fitr',
  eid_adha: 'Eid al-Adha',
  arafah: 'Day of Arafah',
  islamic_new_year: 'Islamic New Year',
  prophet_birthday: "Prophet's Birthday",
  public_holiday: 'Public holiday'
};

function CalendarCard({ data }: { data: Data }) {
  const c = data.calendar;
  return (
    <div
      className="rounded-xl border p-3.5 sm:p-4 space-y-3 min-w-0"
      style={{
        background: 'var(--sig-surface)',
        borderColor: 'var(--sig-border)'
      }}
    >
      <div className="flex items-start gap-2.5">
        <span
          className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: 'var(--sig-tint)' }}
        >
          <CalendarDays
            className="h-4 w-4"
            style={{ color: 'var(--sig-blue)' }}
          />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold">
            Holidays and seasons
          </div>
          <div className="text-[11px] text-muted-foreground">
            {data.country.name} · Gregorian and Hijri dates
          </div>
        </div>
        <StatusPill status={c.status} />
      </div>
      <ul className="divide-y" style={{ borderColor: 'var(--sig-border-row)' }}>
        {c.upcoming.slice(0, 5).map((e) => (
          <li
            key={`${e.type}${e.start}`}
            className="py-2 flex items-center gap-3"
          >
            <div
              className="w-11 shrink-0 rounded-lg text-center py-1"
              style={{ background: 'var(--sig-panel-deep)' }}
            >
              <div className="text-[10px] uppercase text-muted-foreground leading-none">
                {shortDate(e.start).split(' ')[1]}
              </div>
              <div className="text-base font-semibold leading-tight tabular-nums">
                {shortDate(e.start).split(' ')[0]}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium truncate">{e.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {EVENT_LABEL[e.type] ?? e.type}
                {e.end !== e.start ? ` · until ${shortDate(e.end)}` : ''}
              </div>
            </div>
            {e.status === 'expected' ? (
              <span
                className="text-[10.5px] rounded-full px-2 py-0.5 shrink-0"
                style={{
                  background: 'var(--sig-warn-bg)',
                  color: 'var(--sig-warn-text)'
                }}
                title="Date depends on the moon sighting or an official announcement"
              >
                Expected
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── All series list (table on desktop, rows on a phone) ───────────────── */

function SeriesList({
  signals,
  onOpen
}: {
  signals: Signal[];
  onOpen: (s: Signal) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? signals : signals.slice(0, 12);
  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        background: 'var(--sig-surface)',
        borderColor: 'var(--sig-border)'
      }}
    >
      <ul>
        {shown.map((s, i) => (
          <li key={s.key}>
            <button
              type="button"
              onClick={() => onOpen(s)}
              className="w-full text-left px-3.5 py-2.5 flex items-center gap-3 hover:bg-accent/50 transition-colors"
              style={{
                borderTop: i ? '1px solid var(--sig-border-row)' : undefined
              }}
            >
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium truncate">{s.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {s.unit} · {periodLabel(s.latest?.period, s.frequency)}
                </div>
              </div>
              <div className="hidden sm:block w-24 shrink-0">
                <Sparkline values={s.spark} height={26} />
              </div>
              <div className="text-right shrink-0">
                <div className="text-[13px] font-semibold tabular-nums">
                  {formatValue(s.latest?.value, s.unit)}
                </div>
                <div className="text-[11px] text-muted-foreground tabular-nums">
                  {changeText(s, 'year').text} yr
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          </li>
        ))}
      </ul>
      {signals.length > 12 ? (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full py-2.5 text-xs font-medium border-t"
          style={{
            color: 'var(--sig-blue)',
            borderColor: 'var(--sig-border-row)'
          }}
        >
          {expanded ? 'Show fewer' : `Show all ${signals.length}`}
        </button>
      ) : null}
    </div>
  );
}

/* ── Events ─────────────────────────────────────────────────────────────── */

function EventsTimeline({
  events,
  country
}: {
  events: MarketEvent[];
  country: string;
}) {
  if (!events.length) {
    return (
      <Notice tone="info">
        No official market events are recorded for {country} yet. Rate decisions
        and other official changes appear here as their sources are confirmed.
      </Notice>
    );
  }
  return (
    <ol
      className="relative rounded-xl border p-3.5 sm:p-4"
      style={{
        background: 'var(--sig-surface)',
        borderColor: 'var(--sig-border)'
      }}
    >
      {events.slice(0, 12).map((e, i) => {
        const Icon =
          FAMILY_ICON[
            e.type === 'policy_rate'
              ? 'interest_rate'
              : e.type === 'devaluation'
                ? 'fx'
                : e.type === 'fuel_price'
                  ? 'fuel'
                  : 'calendar'
          ] ?? Info;
        return (
          <li
            key={`${e.title}${e.date}`}
            className="relative pl-8 pb-4 last:pb-0"
          >
            {i < Math.min(events.length, 12) - 1 ? (
              <span
                className="absolute left-[11px] top-6 bottom-0 w-px"
                style={{ background: 'var(--sig-border-strong)' }}
              />
            ) : null}
            <span
              className="absolute left-0 top-0.5 h-6 w-6 rounded-full flex items-center justify-center"
              style={{ background: 'var(--sig-tint)' }}
            >
              <Icon
                className="h-3.5 w-3.5"
                style={{ color: 'var(--sig-blue)' }}
              />
            </span>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[13px] font-medium">{e.title}</span>
              <ScopeBadge scope={e.scope} country={country} />
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {shortDate(e.date)} · {e.source}
              {e.derived ? ' · detected from the published series' : ''}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* ── Detail ─────────────────────────────────────────────────────────────── */

const RANGES: [number, string][] = [
  [1, '1Y'],
  [5, '5Y'],
  [30, 'All']
];

function DetailSheet({
  signal: s,
  country,
  onClose
}: {
  signal: Signal | null;
  country: string;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const [years, setYears] = useState(5);
  const [history, setHistory] = useState<SeriesHistory | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!s) return;
    setHistory(null);
    setErr(null);
    apiSeriesHistory(s.key, years)
      .then(setHistory)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [s?.key, years]);

  const dark =
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark');

  return (
    <Sheet open={!!s} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className={
          isMobile
            ? 'h-[92vh] rounded-t-2xl overflow-y-auto px-4 pb-8'
            : 'w-full sm:max-w-xl overflow-y-auto'
        }
      >
        {s ? (
          <div className="space-y-5 pt-2">
            {isMobile ? (
              <div className="mx-auto h-1 w-10 rounded-full bg-muted -mt-1" />
            ) : null}
            <div className="space-y-1.5 pr-6">
              <ScopeBadge scope={s.scope} country={country} />
              <SheetTitle className="text-lg leading-snug">{s.name}</SheetTitle>
              {s.description ? (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {s.description}
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-3 gap-2">
              {/* The unit goes in the caption: inline it truncated "$/troy oz" on a phone. */}
              <Stat
                label={[
                  periodLabel(s.latest?.period, s.frequency),
                  unitLabel(s.unit)
                ]
                  .filter(Boolean)
                  .join(' · ')}
              >
                {formatValue(s.latest?.value, s.unit)}
              </Stat>
              <Stat label={previousLabel(s.frequency)}>
                {changeText(s, 'previous').text}
              </Stat>
              <Stat label="vs a year ago">{changeText(s, 'year').text}</Stat>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  History
                </span>
                <div
                  className="inline-flex rounded-lg p-0.5"
                  style={{ background: 'var(--sig-tab-active)' }}
                >
                  {RANGES.map(([y, label]) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setYears(y)}
                      className="px-2.5 py-1 text-xs rounded-md"
                      style={{
                        background:
                          years === y ? 'var(--sig-surface)' : 'transparent',
                        fontWeight: years === y ? 600 : 400,
                        color:
                          years === y ? 'var(--sig-ink)' : 'var(--sig-muted)'
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div
                className="h-56 rounded-xl border p-1"
                style={{ borderColor: 'var(--sig-border)' }}
              >
                {err ? (
                  <p className="text-sm text-muted-foreground p-3">{err}</p>
                ) : !history ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <Suspense fallback={null}>
                    <ReactApexChart
                      type="area"
                      height="100%"
                      series={[
                        {
                          name: s.name,
                          data: history.points.map(([d, v]) => ({
                            x: new Date(d).getTime(),
                            y: s.unit?.startsWith('share')
                              ? +(v * 100).toFixed(2)
                              : v
                          }))
                        }
                      ]}
                      options={{
                        chart: {
                          toolbar: { show: false },
                          zoom: { enabled: false },
                          animations: { enabled: false },
                          background: 'transparent',
                          fontFamily: 'inherit'
                        },
                        theme: { mode: dark ? 'dark' : 'light' },
                        colors: ['#0080e0'],
                        stroke: { width: 2, curve: 'straight' },
                        fill: {
                          type: 'gradient',
                          gradient: { opacityFrom: 0.28, opacityTo: 0.02 }
                        },
                        dataLabels: { enabled: false },
                        grid: {
                          borderColor: dark ? '#2a3440' : '#edf1f5',
                          strokeDashArray: 3
                        },
                        xaxis: {
                          type: 'datetime',
                          labels: { style: { fontSize: '10px' } },
                          axisBorder: { show: false },
                          axisTicks: { show: false }
                        },
                        yaxis: {
                          labels: {
                            style: { fontSize: '10px' },
                            formatter: (v: number) =>
                              s.unit?.startsWith('share')
                                ? `${v.toFixed(0)}%`
                                : formatValue(v, s.unit)
                          }
                        },
                        tooltip: {
                          x: {
                            format:
                              s.frequency === 'daily'
                                ? 'dd MMM yyyy'
                                : 'MMM yyyy'
                          }
                        }
                      }}
                    />
                  </Suspense>
                )}
              </div>
            </div>

            {history?.recent.length ? (
              <div className="space-y-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Latest figures
                </span>
                <div
                  className="rounded-xl border overflow-hidden"
                  style={{ borderColor: 'var(--sig-border)' }}
                >
                  <table className="w-full text-[13px]">
                    <tbody>
                      {history.recent.map((r, i) => (
                        <tr
                          key={r.period}
                          style={{
                            borderTop: i
                              ? '1px solid var(--sig-border-row)'
                              : undefined
                          }}
                        >
                          <td className="px-3 py-1.5 text-muted-foreground">
                            {periodLabel(r.period, s.frequency)}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                            {formatValue(r.value, s.unit)}
                            {r.provisional ? (
                              <span
                                className="ml-1.5 text-[10px] text-muted-foreground"
                                title="May still be revised by the publisher"
                              >
                                provisional
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {s.used_for ? (
              <div
                className="rounded-xl p-3 text-sm"
                style={{ background: 'var(--sig-tint)' }}
              >
                <div
                  className="text-[11px] uppercase tracking-wide font-medium mb-1"
                  style={{ color: 'var(--sig-blue-deep)' }}
                >
                  What Signals uses it for
                </div>
                {s.used_for}
              </div>
            ) : null}

            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-[13px]">
              <dt className="text-muted-foreground">Source</dt>
              <dd className="min-w-0">
                {s.source_url ? (
                  <a
                    href={s.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 underline-offset-2 hover:underline break-all"
                    style={{ color: 'var(--sig-blue)' }}
                  >
                    {s.source}
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                ) : (
                  s.source
                )}
              </dd>
              <dt className="text-muted-foreground">Updates</dt>
              <dd>{frequencyLabel(s.frequency)}</dd>
              <dt className="text-muted-foreground">History</dt>
              <dd>
                {s.coverage_from
                  ? `Since ${periodLabel(s.coverage_from, s.frequency)}`
                  : '—'}{' '}
                · {s.points.toLocaleString('en-US')} figures
              </dd>
              <dt className="text-muted-foreground">Last refreshed</dt>
              <dd>{relativeTime(s.updated_at)}</dd>
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <StatusPill status={s.status} />
              </dd>
            </dl>

            {s.attribution ? (
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {s.attribution}
              </p>
            ) : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function Stat({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-xl border p-2.5 min-w-0"
      style={{
        background: 'var(--sig-panel)',
        borderColor: 'var(--sig-border)'
      }}
    >
      <div className="text-base sm:text-lg font-semibold tabular-nums truncate">
        {children}
      </div>
      <div className="text-[11px] text-muted-foreground truncate">{label}</div>
    </div>
  );
}
